/**
 * Binary frame types and constants.
 *
 * A binary message is a WebSocket binary frame carrying a JSON header and a
 * single raw payload. Used for file/blob transfer. Big-endian.
 *
 * ```
 * | ver(1B) | hdrLen(2B, BE) | header (UTF-8 JSON, hdrLen B) | payload |
 * ```
 *
 * - `ver` — format version byte (currently {@link BINARY_FRAME_VERSION}). This
 *   is the single source of truth for the frame's version.
 * - `hdrLen` — byte length of the JSON header. Max {@link MAX_HEADER_BYTES}.
 * - `header` — JSON mirroring the text {@link Message} envelope (minus `ver`),
 *   plus an optional `payloadMeta`.
 * - `payload` — raw bytes (a single blob). The frame is self-delimiting: the
 *   payload runs from offset `3 + hdrLen` to the end of the frame.
 *
 * This package defines the format; encoding/decoding is the responsibility of
 * implementations (e.g. `pigeon-room`, `pigeon-link`).
 */

import type { Message, RoomStamped } from "./text.ts";

/** Current binary format version, written to the leading `ver` byte. */
export const BINARY_FRAME_VERSION = 1;

/** Maximum JSON header size in bytes (2-byte length field). */
export const MAX_HEADER_BYTES = 0xffff; // 65535

/**
 * The JSON header of a binary frame. Mirrors the text envelope, plus an
 * optional `payloadMeta`. The header JSON does not normatively carry a `ver`
 * field; if one appears, the leading `ver` byte takes precedence.
 *
 * `payloadMeta` describes the payload and is provided by the sender as a
 * courtesy — it is not needed to parse the frame (the payload is
 * self-delimiting). Its content is application-defined (opaque to the spec); a
 * common convention is `{ name, mimeType }`.
 */
export type BinaryFrameHeader<TBody = unknown> =
  & Message<TBody>
  & Partial<RoomStamped>
  & { payloadMeta?: unknown };

/**
 * The shape a parsed binary frame should produce. `ver` is taken from the `ver`
 * byte and is authoritative.
 */
export interface ParsedBinaryFrame<TBody = unknown> {
  /** Format version, from the `ver` byte. */
  ver: number;
  header: BinaryFrameHeader<TBody>;
  payload: Uint8Array;
}
